import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Bell, User, ShoppingBag, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';

const navLinkClass = ({ isActive }) =>
  `text-xs font-bold tracking-[0.2em] uppercase transition-colors ${
    isActive ? 'text-white' : 'text-zinc-400 hover:text-white'
  }`;

const DashboardHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifMenuOpen, setNotifMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notifMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (notifMenuRef.current && !notifMenuRef.current.contains(e.target)) {
        setNotifMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    setNotifMenuOpen(false);
    await logout();
    navigate('/');
  };

  const initial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <header className="h-16 border-b border-white/10 bg-black/90 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto h-full px-6 md:px-10 flex items-center justify-between gap-6">
        <div className="flex items-center gap-8 min-w-0">
          <Link to="/my-learning" className="flex items-center gap-2 shrink-0" aria-label="My Learning">
            <Logo className="w-14 h-9" />
            <span className="brand-wordmark hidden sm:inline">CYNOPSIS</span>
          </Link>
          <nav className="flex items-center">
            <NavLink to="/my-learning" end className={navLinkClass} data-testid="nav-my-learning">
              MY LEARNING
            </NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="relative" ref={notifMenuRef}>
            <button
              type="button"
              onClick={() => {
                setNotifMenuOpen((o) => !o);
                setUserMenuOpen(false);
              }}
              className={`p-2 rounded-full transition-all ${
                notifMenuOpen
                  ? 'text-white bg-white/10 ring-1 ring-white/20'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
              aria-label="Notifications"
              aria-expanded={notifMenuOpen}
              aria-haspopup="true"
              data-testid="dashboard-notifications"
            >
              <Bell className="w-5 h-5" />
            </button>

            {notifMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-72 py-2 bg-[#0D0D0D] border border-white/10 shadow-2xl z-50"
                role="menu"
              >
                <p className="px-4 py-2 text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500">
                  Notifications
                </p>
                <div className="px-4 py-6 text-center border-t border-white/5">
                  <p className="text-sm text-zinc-400">No new notifications</p>
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => {
                setUserMenuOpen((o) => !o);
                setNotifMenuOpen(false);
              }}
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                userMenuOpen
                  ? 'ring-2 ring-[#C8102E] bg-[#C8102E]/20 text-white'
                  : 'bg-[#1a1a1a] border border-white/15 text-zinc-200 hover:border-[#C8102E]/40 hover:bg-[#C8102E]/10'
              }`}
              data-testid="dashboard-user-menu"
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
              aria-label="Account menu"
            >
              {initial}
            </button>

            {userMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-52 py-2 bg-[#0D0D0D] border border-white/10 shadow-2xl z-50"
                role="menu"
              >
                <Link
                  to="/profile"
                  role="menuitem"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                  data-testid="menu-profile"
                >
                  <User className="w-4 h-4 text-zinc-500" />
                  Profile
                </Link>
                <Link
                  to="/purchases"
                  role="menuitem"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                  data-testid="menu-purchases"
                >
                  <ShoppingBag className="w-4 h-4 text-zinc-500" />
                  My Purchases
                </Link>
                <div className="my-2 border-t border-white/5" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors text-left"
                  data-testid="dashboard-logout"
                >
                  <LogOut className="w-4 h-4 text-zinc-500" />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
